import { apiCall } from './api';
import { getObj, updateObj } from './localStorage';
import { logError } from './sentry';
import activeCountries from './assets/active_countries.json';
import { getUrlParameter, updateUrlParameter } from './window';
import { pushEvent } from './ecommerceTracking';
import { countryFromTimezone, resolveTrustedCountry } from './currency-region';
import { getTimeZone } from './timezone';
import { getUserLanguage } from './language';

const normalizeCountryCode = (code) => {
  if (!code || typeof code !== 'string') return null;
  const upper = code.toUpperCase();
  if (upper === 'UNDEFINED' || upper === 'NULL') return null;
  return /^[A-Z]{2}$/.test(upper) ? upper : null;
};

const isSupportedCountry = (code) =>
  !!code && activeCountries.some((c) => c.countryCode === code.toUpperCase());

// How a stored country came to be, strongest first:
//   'user'    — the visitor picked it (nav chip, modal). A deliberate statement
//               about where they are, so it outranks everything, including a
//               profile that disagrees (KL-15620).
//   'profile' — mirrored from the logged-in profile so the chip can paint before
//               the deferred bundle runs. Re-read from the profile every load,
//               never treated as a pick.
//   'geo'     — an inference, re-checked against the trust rules on every read.
// Values stored before this distinction existed have no source, so they are
// treated as inferences.
const SOURCE_USER = 'user';
const SOURCE_PROFILE = 'profile';

const updateCountryInLocalStorage = (countryCode, countrySource = 'geo') => {
  updateObj('funnel', {
    countryCode,
    countrySource,
    // Mirroring the profile establishes nothing new, so it leaves a fallback
    // marker standing; a pick or a corroborated detection resolves it.
    ...(countrySource === SOURCE_PROFILE ? {} : { countryFallback: false }),
  });
  updateUrlParameter('country', countryCode);
  pushEvent({ event: 'set_country', country: countryCode });
  // Tell the static nav chrome what we settled on. It renders the flag and the
  // payment icons but resolves nothing itself — see the country-view note in
  // site/dist/assets/nav/nav.js.
  if (typeof document !== 'undefined') {
    document.dispatchEvent(new CustomEvent('klikkie:country', { detail: { country: countryCode } }));
  }
  console.log(`Country ${countryCode} updated in local storage`);
};

const getSavedCountry = () => {
  const rawSaved = getObj('funnel').countryCode || getUrlParameter('country');
  return normalizeCountryCode(rawSaved);
};

// A `?country=` in the URL is never an explicit choice: it is written there by
// this module, so it rides along in any link the visitor copies or shares.
const isSavedCountryExplicit = () =>
  !!getObj('funnel').countryCode && getObj('funnel').countrySource === SOURCE_USER;

const getProfileCountry = () => normalizeCountryCode(getObj('user')?.profile?.countryCode);

/**
 * Record that a country had to be invented rather than resolved.
 *
 * Profile creation must send one — CIO alerts on a profile with no country —
 * but a value nobody established is not an answer, and a profile country
 * outranks everything below it. Without this marker the default silences the
 * very modal that exists to replace it, and the visitor is Dutch for good.
 */
export const markCountryAsFallback = () => {
  updateObj('funnel', { countryFallback: true });
};

const isCountryFallback = () => getObj('funnel').countryFallback === true;

// Countries where SEPA Direct Debit is offered. Narrower than the set of
// countries that accept SEPA in general — these are the markets where the
// Adyen risk profile lets us collect by direct debit (lower fraud risk).
// Curated business rule, not derivable from active_countries.json's
// paymentMethods.allowed (which marks SEPA payable on ~23 EU markets, of
// which only this subset is approved for direct debit).
export const COUNTRIES_WITH_SEPA_DIRECT_DEBIT = ['DE', 'ES', 'AT'];

// Default market when getCountry() yields null (GeoIP failure / unsupported
// country → it drives the country-selection modal). Used wherever a value must
// never be null — profile creation, CIO lead forms — so people always carry a
// country. Matches the `|| 'NL'` / `.catch(() => 'NL')` fallbacks already inlined
// at other getCountry() call sites.
export const DEFAULT_COUNTRY_CODE = 'NL';

/**
 * Apply the currency-region trust rules to a country we inferred rather than
 * were told. Explicit values (profile, modal pick) never pass through here.
 */
const trust = (countryCode) => {
  const resolved = resolveTrustedCountry({
    detected: countryCode,
    lang: getUserLanguage(),
    timeZone: getTimeZone(),
  });
  return isSupportedCountry(resolved) ? resolved : null;
};

// One request per burst. Callers that arrive while a lookup is in flight share
// it; the slot clears when it settles, so a later call can still retry after a
// failure. Deliberately not a document-lifetime cache — that would make a
// transient failure permanent for the page.
let inFlightGeoIP = null;

const requestGeoIP = async () => {
  try {
    const { country } = await apiCall(
      'https://europe-west1-klikkie-prod.cloudfunctions.net/geoIpTS-getCountryFromIp',
      'GET',
      undefined,
      false
    );
    const trusted = trust(country);
    return { country: trusted || country, isSupported: !!trusted };
  } catch (error) {
    logError(error);
    return { country: null, isSupported: false };
  }
};

const detectCountryFromGeoIP = () => {
  if (inFlightGeoIP) return inFlightGeoIP;
  inFlightGeoIP = requestGeoIP().finally(() => {
    inFlightGeoIP = null;
  });
  return inFlightGeoIP;
};

// KL-16009: the browser's own clock, once the IP lookup has nothing to offer.
// IP comes first because it names the country; a zone only names the region
// the clock is set to. Zones we don't sell to have no opinion.
const supportedTimezoneCountry = () => {
  const fromZone = countryFromTimezone(getTimeZone());
  return isSupportedCountry(fromZone) ? fromZone : null;
};

/**
 * Resolve the user's country. Subdomain is intentionally NOT consulted —
 * subdomain encodes the language the visitor chose to read in, not where they
 * live (KL-13563). Priority:
 *   1. An explicit pick (nav chip / modal)
 *   2. Logged-in user profile (stable across sessions, still beats GeoIP)
 *   3. Saved localStorage / URL parameter (prior detection — re-validated)
 *   4. GeoIP (anonymous best-effort)
 *   5. Browser timezone, when GeoIP fails or names a country we don't sell to
 *   6. null → trigger country selection modal
 */
export const getCountry = async () => {
  const savedCountry = getSavedCountry();

  // A pick outranks the profile it disagrees with: the visitor is telling us
  // where they are, and updateCountry() pushes the pick to the profile so the
  // two converge rather than fight. Before KL-15620 the profile branch ran
  // first and wrote back, so picking a country repainted the page and was then
  // silently reverted on the next navigation.
  if (savedCountry && isSupportedCountry(savedCountry) && isSavedCountryExplicit()) {
    return savedCountry;
  }

  const profileCountry = getProfileCountry();
  if (profileCountry && isSupportedCountry(profileCountry)) {
    // Mirror it so the nav chip can paint from storage on the next load, but
    // only when it actually changed. This used to run on every call, and a
    // product listing calls getCountry() once per price element — 331 of them
    // on /products/photobooks — so it was 331 writes and 331 `klikkie:country`
    // dispatches per page load.
    if (normalizeCountryCode(getObj('funnel').countryCode) !== profileCountry) {
      updateCountryInLocalStorage(profileCountry, SOURCE_PROFILE);
    }
    return profileCountry;
  }

  if (savedCountry && isSupportedCountry(savedCountry)) {
    // Everything reaching here is an inference (a pick returned above), so it
    // is re-checked on every read: a country stored before these rules existed
    // — or carried in on a shared `?country=` link — is corrected rather than
    // inherited forever.
    const trusted = trust(savedCountry);
    if (trusted) {
      if (trusted !== savedCountry) updateCountryInLocalStorage(trusted);
      return trusted;
    }
    console.warn(`Saved country '${savedCountry}' is not corroborated here, re-detecting`);
  } else if (savedCountry) {
    console.warn(
      `Saved/URL country '${savedCountry}' is not supported, falling back to GeoIP`
    );
  }

  const { country, isSupported } = await detectCountryFromGeoIP();
  if (isSupported) {
    updateCountryInLocalStorage(country);
    return country;
  }

  const zoneCountry = supportedTimezoneCountry();
  if (zoneCountry) {
    // Callers that shared one failed lookup all land here; store it once.
    if (normalizeCountryCode(getObj('funnel').countryCode) !== zoneCountry) {
      updateCountryInLocalStorage(zoneCountry);
    }
    return zoneCountry;
  }

  if (country) {
    console.log(`Country "${country}" is not supported, will show country selection modal`);
  } else {
    console.warn('Country detection failed, will show country selection modal');
  }
  return null;
};

export const shouldShowCountryModal = async () => {
  // Nothing has established a country — one was invented to keep the profile
  // complete. Neither that profile nor the storage mirrored from it counts as
  // an answer, so ask. Any real write (a pick, a corroborated detection)
  // clears the marker.
  if (isCountryFallback()) return true;

  const profileCountry = getProfileCountry();
  if (profileCountry && isSupportedCountry(profileCountry)) return false;

  const savedCountry = getSavedCountry();
  if (savedCountry && isSupportedCountry(savedCountry)) {
    if (isSavedCountryExplicit()) return false;
    if (trust(savedCountry)) return false;
    return true;
  }

  // An unsupported saved country is passed over, as getCountry() does: the
  // lookup and the timezone decide (KL-16009).
  const { isSupported } = await detectCountryFromGeoIP();
  return !isSupported && !supportedTimezoneCountry();
};

/**
 * Accept country picks from the static nav chrome. The pick is validated here
 * like any other explicit choice — nav sends an intent, it does not decide.
 * The queued value covers picks made before this deferred bundle attached.
 */
export const listenForCountryPicks = () => {
  if (typeof document === 'undefined') return;
  document.addEventListener('klikkie:country-pick', (event) => {
    updateCountry(event?.detail?.country);
  });
  if (window.__klikkieCountryPick) {
    updateCountry(window.__klikkieCountryPick);
    delete window.__klikkieCountryPick;
  }
};

/**
 * Push a pick to the logged-in profile so the two stop disagreeing. The pick
 * now outranks the profile locally (KL-15620), but the profile is what every
 * server-side decision reads — order currency, emails — so leaving it on the
 * signup country means the visitor browses in USD and is charged in EUR.
 *
 * `user.js` is imported dynamically: it pulls in Firebase auth, and importing
 * it statically would both drag that into this lightweight module and close
 * the country <-> user cycle. It reads the country back through getCountry(),
 * which is why the local write has to happen first.
 */
const syncCountryToProfile = async () => {
  if (!getObj('user')?.userId) return; // anonymous — nothing to sync to
  try {
    const { updateUser } = await import('./user');
    await updateUser();
  } catch (error) {
    // Safe to swallow: the pick is already stored as an explicit choice and
    // wins on the next read, so the visitor keeps their currency either way.
    console.warn('Failed to sync country to profile', error);
  }
};

export const updateCountry = async (countryCode) => {
  const normalized = normalizeCountryCode(countryCode);
  if (!normalized || !isSupportedCountry(normalized)) {
    console.warn(`Attempted to set unsupported country '${countryCode}', ignoring`);
    return;
  }
  updateCountryInLocalStorage(normalized, SOURCE_USER);
  await syncCountryToProfile();
};
